type Rgb = [number, number, number];

const FALLBACK_TEXT = '#394050';

const parseHex = (hex: string | undefined | null): Rgb | null => {
  if (!hex) return null;
  const clean = hex.replace(/^#/, '');
  const six = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean.length === 6 ? clean : null;
  if (!six) return null;
  const n = parseInt(six, 16);
  return Number.isNaN(n) ? null : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const luminanceOf = (rgb: Rgb): number => {
  const [r, g, b] = rgb.map(v => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const hexToRgb = (hex: string): Rgb => {
  const rgb = parseHex(hex);
  if (!rgb) throw new Error('Invalid hex color format');
  return rgb;
};

export const getLuminance = (hex: string): number => luminanceOf(hexToRgb(hex));

export const isLight = (hex: string | undefined): boolean => {
  const rgb = parseHex(hex);
  return rgb ? luminanceOf(rgb) > 0.5 : false;
};

export const getTextColor = (hex: string, light = '#ffffff', dark = '#59647A'): string =>
  isLight(hex) ? dark : light;

export const hexToRgba = (hex: string | undefined, alpha: number): string => {
  const rgb = parseHex(hex);
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : 'transparent';
};

export const getSeverityColor = (color?: string | null): string => {
  if (!color) return 'transparent';
  const hex = color.startsWith('#') ? color : `#${color}`;
  if (hex.length === 9) return hex;
  return hexToRgba(hex, 0.7);
};

export const getReadableText = (hex: string | undefined): string => {
  const rgb = parseHex(hex);
  if (!rgb) return FALLBACK_TEXT;
  const l = luminanceOf(rgb);
  const k = 1 - (l > 0.6 ? 0.7 : l > 0.35 ? 0.45 : 0.25);
  return `rgb(${rgb.map(c => Math.max(0, Math.round(c * k))).join(', ')})`;
};

export const getSaturatedDark = (hex: string | undefined): string => {
  const rgb = parseHex(hex);
  if (!rgb) return FALLBACK_TEXT;
  const [r, g, b] = rgb.map(c => c / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (!d) return 'rgb(89, 89, 89)';
  const h = ((max === r ? (g - b) / d + (g < b ? 6 : 0)
    : max === g ? (b - r) / d + 2
      : (r - g) / d + 4) / 6);
  const s = Math.min(1, (max + min > 1 ? d / (2 - max - min) : d / (max + min)) * 1.2);
  const L = 0.35;
  const q = L < 0.5 ? L * (1 + s) : L + s - L * s;
  const p = 2 * L - q;
  const c = (t: number): number => {
    t = ((t % 1) + 1) % 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const px = (n: number) => Math.round(n * 255);
  return `rgb(${px(c(h + 1 / 3))}, ${px(c(h))}, ${px(c(h - 1 / 3))})`;
};
